import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { FileUpload } from "primereact/fileupload";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { AuthContext } from "../../../../Header/auth - context";
import CustomDropdown from "../../../../custom/customDropdown";
import handleStampaDokumenta from "../../../../custom/dokumentStampa";

/* Datum ide kroz toUtcIso — bez korekcije offseta toISOString ume da pomeri dan
   unazad (vidi CLAUDE.md). */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

/* Fajl na disku (C:/Dokumenta/...) -> URL koji servira backend (/API/files/...) */
const linkDokumenta = (putanja) => {
    if (!putanja) return null;
    const normalizovana = String(putanja).replace(/\\/g, "/");
    const relativna = normalizovana
        .replace(/^([A-Za-z]:)?\/Dokumenta\//, "")
        .replace(/^\/var\/www\/dokumenta\//, "");
    return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(relativna)}`;
};

const praznaForma = {
    id_zaposleni: null,
    tip_dokumenta: null,
    datum_dokumenta: new Date(),
    datum_potpisa: new Date(),
    vazi_od: null,
    vazi_do: null,
    broj_polise: "",
    osiguravac: "",
    zavedeni_broj: "",
    napomena: "",
};

/* Tab UNOS — ovde dokument postaje zvaničan.
   Štampa (tab ŠTAMPA) ništa ne upisuje; tek kad se potpisan primerak skenira i
   okači ovde, dokument dobija redni broj i ulazi u evidenciju. */
const UnosTab = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector((state) => state.company);
    const toast = useRef(null);

    const [redovi, setRedovi] = useState([]);
    const [tipoviDB, setTipoviDB] = useState([]);
    const [zaposleniDB, setZaposleniDB] = useState([]);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [ucitava, setUcitava] = useState(false);
    const [salje, setSalje] = useState(false);
    const [forma, setForma] = useState(praznaForma);
    const [fajl, setFajl] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const izmeni = (polje, vrednost) => setForma((prev) => ({ ...prev, [polje]: vrednost }));

    /* Obavezna polja se crvene tek posle pokušaja slanja — isto kao u
       ZAPOSLENI > UNOS. Zato nema zvezdica uz labele. */
    const invalidControl = (v) => formSubmitted && (v === null || v === undefined || v === "");

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 4000 });

    const zaglavlje = useCallback(() => ({
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
    }), [auth.token]);

    const posalji = useCallback(async (step, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/employees/stampaDokumentacije", {
            method: "POST",
            headers: zaglavlje(),
            body: JSON.stringify({ step, ...telo }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.message || t("stampaDok.greska"));
        return data;
    }, [zaglavlje, t]);

    const ucitaj = useCallback(async () => {
        setUcitava(true);
        try {
            const d = await posalji("INIT");
            setTipoviDB(d.TipoviDokumenata || []);
            setZaposleniDB(d.ZaposleniDB || []);
            setRedovi(d.DokumentaDB || []);
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        } finally {
            setUcitava(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posalji, t]);

    useEffect(() => { ucitaj(); }, [ucitaj]);

    const izabraniTip = tipoviDB.find((x) => x.value === forma.tip_dokumenta);
    const nemaSablon = !!izabraniTip && !izabraniTip.imaSablon;
    const jePZO = /osiguran/i.test(String(izabraniTip?.label || ""));
    const jeSporazum = /sporazum|prestank/i.test(String(izabraniTip?.label || ""));

    const zavedi = async () => {
        setFormSubmitted(true);
        /* Bez toasta — prazna obavezna polja se već crvene kroz invalid. */
        if (!forma.id_zaposleni || !forma.tip_dokumenta || !fajl) return;

        setSalje(true);
        try {
            const telo = new FormData();
            telo.append("potpisan", fajl);
            /* multer traži id + entityType da bi odredio folder na disku */
            telo.append("id", forma.id_zaposleni);
            telo.append("entityType", "dokumentacija");

            telo.append("id_zaposleni", forma.id_zaposleni);
            telo.append("tip_dokumenta", forma.tip_dokumenta);
            telo.append("naziv_dokumenta", izabraniTip?.label || "");
            telo.append("datum_dokumenta", toUtcIso(forma.datum_dokumenta) || "");
            telo.append("datum_potpisa", toUtcIso(forma.datum_potpisa) || "");
            telo.append("vazi_od", toUtcIso(forma.vazi_od) || "");
            telo.append("vazi_do", toUtcIso(forma.vazi_do) || "");
            telo.append("broj_polise", forma.broj_polise || "");
            telo.append("osiguravac", forma.osiguravac || "");
            telo.append("napomena", forma.napomena || "");
            telo.append("zavedeni_broj", forma.zavedeni_broj || "");

            const r = await fetch(
                process.env.REACT_APP_BACKEND_URL + "/employees/stampaDokumentacije/potpisan",
                { method: "POST", headers: { Authorization: "Bearer " + auth.token }, body: telo }
            );
            const data = await r.json();
            if (!r.ok) throw new Error(data.message || t("stampaDok.greska"));

            /* Šablon za firmu ne postoji — isto ponašanje kao kod štampe ugovora */
            if (data.message) {
                poruka("warn", t("stampaDok.naslovUnos"), data.message);
                return;
            }

            poruka("success", t("stampaDok.naslovUnos"),
                t("stampaDok.zavedenoPoruka", { broj: data.zavedeniBroj }));
            setForma({ ...praznaForma, datum_dokumenta: new Date(), datum_potpisa: new Date() });
            setFajl(null);
            setFormSubmitted(false);
            ucitaj();
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        } finally {
            setSalje(false);
        }
    };

    /* Ponovna štampa ide iz snapshota — papir mora ostati identičan onome što je
       zavedeno, čak i ako se šablon u međuvremenu promeni. */
    const ponoviStampu = async (red) => {
        try {
            const r = await posalji("PONOVI", { id: red.id });
            if (r.message) {
                poruka("warn", t("stampaDok.naslovUnos"), r.message);
                return;
            }
            handleStampaDokumenta(r);
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        }
    };

    const obrisi = (red) => {
        confirmDialog({
            message: t("stampaDok.brisanjePitanje", {
                tip: red.TipDokumenta, broj: red.zavedeni_broj, zaposleni: red.Zaposleni,
            }),
            header: t("stampaDok.brisanjeNaslov"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.da"),
            rejectLabel: t("common.ne"),
            accept: async () => {
                try {
                    const r = await posalji("DELETE", { id: red.id });
                    poruka("success", t("stampaDok.naslovUnos"), r.message || t("stampaDok.obrisano"));
                    ucitaj();
                } catch (e) {
                    poruka("error", t("common.greska"), e.message);
                }
            },
        });
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Dokumentacija");
        const headers = [
            t("stampaDok.k_Zaposleni"), t("stampaDok.k_JMBG"), t("stampaDok.k_Tip"),
            t("stampaDok.k_RB"), t("stampaDok.k_ZavedeniBroj"), t("stampaDok.k_Datum"),
            t("stampaDok.k_DatumPotpisa"), t("stampaDok.k_VaziOd"), t("stampaDok.k_VaziDo"),
            t("stampaDok.k_Napomena"), t("stampaDok.k_Zavedeno"),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (redovi || []).forEach((r) => {
            const row = worksheet.addRow([
                r.Zaposleni, r.JMBG, r.TipDokumenta, r.redni_broj, r.zavedeni_broj,
                r.DatumDokumenta, r.DatumPotpisa, r.VaziOd, r.VaziDo, r.napomena, r.Zavedeno,
            ].map((v) => v ?? ""));
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
        }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `${t("stampaDok.naslov")} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div style={{ display: "flex", gap: "0.4rem" }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                <Button type="button" icon="pi pi-refresh" rounded onClick={ucitaj} data-pr-tooltip={t("stampaDok.osvezi")} />
            </div>
            <input type="search" placeholder={t("common.search")} className="p-inputtext p-component"
                onInput={(e) => setGlobalFilter(e.target.value)} />
        </div>
    );

    const kolonaFajlovi = (r) => (
        <div style={{ display: "flex", gap: "0.25rem" }}>
            {r.putanja_potpisan && (
                <Button type="button" icon="pi pi-verified" className="p-button-text p-button-sm p-button-success"
                    tooltip={t("stampaDok.otvoriPotpisan")} tooltipOptions={{ position: "top" }}
                    onClick={() => window.open(linkDokumenta(r.putanja_potpisan), "_blank")} />
            )}
            {r.putanja && (
                <Button type="button" icon="pi pi-file" className="p-button-text p-button-sm"
                    tooltip={t("stampaDok.otvoriOriginal")} tooltipOptions={{ position: "top" }}
                    onClick={() => window.open(linkDokumenta(r.putanja), "_blank")} />
            )}
            <Button type="button" icon="pi pi-print" className="p-button-text p-button-sm"
                tooltip={t("stampaDok.stampaj")} tooltipOptions={{ position: "top" }}
                onClick={() => ponoviStampu(r)} />
            <Button type="button" icon="pi pi-trash" className="p-button-text p-button-sm p-button-danger"
                tooltip={t("stampaDok.obrisi")} tooltipOptions={{ position: "top" }}
                onClick={() => obrisi(r)} />
        </div>
    );

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <div className="stampaDokForma">
                        <div className="sd-polje">
                            <label>{t("stampaDok.zaposleni")}:</label>
                            <CustomDropdown style={{ width: "100%" }} options={zaposleniDB}
                                value={forma.id_zaposleni} optionLabel="label"
                                invalid={invalidControl(forma.id_zaposleni)}
                                placeholder={t("stampaDok.izaberiZaposlenog")}
                                onChange={(e) => izmeni("id_zaposleni", e.target.value)} />
                        </div>

                        <div className="sd-polje">
                            <label>{t("stampaDok.tipDokumenta")}:</label>
                            <CustomDropdown style={{ width: "100%" }} options={tipoviDB}
                                value={forma.tip_dokumenta} optionLabel="label"
                                invalid={invalidControl(forma.tip_dokumenta)}
                                placeholder={t("stampaDok.izaberiTip")}
                                emptyMessage={t("stampaDok.nemaTipovaZaFirmu")}
                                onChange={(e) => izmeni("tip_dokumenta", e.target.value)} />
                        </div>

                        {/* Ako ostane prazno, sistem sam sastavi broj. */}
                        <div className="sd-polje">
                            <label>{t("stampaDok.zavedeniBroj")}:</label>
                            <InputText value={forma.zavedeni_broj} style={{ width: "100%" }}
                                placeholder={t("stampaDok.zavedeniBrojPh")}
                                onChange={(e) => izmeni("zavedeni_broj", e.target.value)} />
                        </div>

                        <div className="sd-polje">
                            <label>{t("stampaDok.datumDokumenta")}:</label>
                            <Calendar value={forma.datum_dokumenta} onChange={(e) => izmeni("datum_dokumenta", e.value)}
                                dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }} />
                        </div>

                        {jePZO && (
                            <>
                                <div className="sd-polje">
                                    <label>{t("stampaDok.brojPolise")}:</label>
                                    <InputText value={forma.broj_polise} style={{ width: "100%" }}
                                        onChange={(e) => izmeni("broj_polise", e.target.value)} />
                                </div>
                                <div className="sd-polje">
                                    <label>{t("stampaDok.osiguravac")}:</label>
                                    <InputText value={forma.osiguravac} style={{ width: "100%" }}
                                        onChange={(e) => izmeni("osiguravac", e.target.value)} />
                                </div>
                            </>
                        )}

                        {/* Sporazum ne traži datum zasnivanja — vuče se iz kartona zaposlenog */}
                        {!jeSporazum && (
                            <div className="sd-polje">
                                <label>{jePZO ? t("stampaDok.osiguranjeOd") : t("stampaDok.vaziOd")}:</label>
                                <Calendar value={forma.vazi_od} onChange={(e) => izmeni("vazi_od", e.value)}
                                    dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }} />
                            </div>
                        )}

                        <div className="sd-polje">
                            <label>{jePZO ? t("stampaDok.osiguranjeDo")
                                : jeSporazum ? t("stampaDok.datumPrestanka") : t("stampaDok.vaziDo")}:</label>
                            <Calendar value={forma.vazi_do} onChange={(e) => izmeni("vazi_do", e.value)}
                                dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }}
                                minDate={forma.vazi_od || undefined} />
                        </div>

                        <div className="sd-polje">
                            <label>{t("stampaDok.datumPotpisa")}:</label>
                            <Calendar value={forma.datum_potpisa} onChange={(e) => izmeni("datum_potpisa", e.value)}
                                dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }} />
                        </div>

                        {/* Isti obrazac kao upload u ZAPOSLENI > UNOS */}
                        <div className="sd-polje">
                            <label>{t("stampaDok.skeniranPrimerak")}:</label>
                            {!fajl ? (
                                <FileUpload
                                    name="potpisan"
                                    className={invalidControl(fajl) ? "p-invalid" : ""}
                                    chooseLabel={t("admin.izaberi")}
                                    customUpload={false}
                                    auto={false}
                                    mode="basic"
                                    accept=".pdf,.jpg,.png"
                                    maxFileSize={5000000}
                                    onSelect={(e) => setFajl(e.files[0])}
                                />
                            ) : (
                                <Button type="button" label={fajl.name} onClick={() => setFajl(null)} />
                            )}
                        </div>

                        <div className="sd-polje sd-puno">
                            <label>{t("stampaDok.napomena")}:</label>
                            <InputText value={forma.napomena} style={{ width: "100%" }}
                                onChange={(e) => izmeni("napomena", e.target.value)} />
                        </div>

                        {nemaSablon && (
                            <small className="sd-puno" style={{ color: "#d9534f" }}>
                                {t("stampaDok.nemaSablonaUpozorenje")}
                            </small>
                        )}

                        <small className="sd-puno" style={{ color: "#6c757d" }}>
                            {t("stampaDok.unosNapomena")}
                        </small>

                        <div className="sd-puno sd-akcija">
                            <button type="button" onClick={zavedi} disabled={salje}
                                className="btn btn-success">
                                {t("stampaDok.sacuvajPotpisan")}
                            </button>
                        </div>
                    </div>
                </Card>

                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable
                        value={redovi || []}
                        loading={ucitava}
                        paginator rows={10}
                        globalFilter={globalFilter}
                        scrollable scrollHeight="50vh"
                        header={tableHeader}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable stavke-tabela"
                        emptyMessage={t("stampaDok.nemaPodataka")}
                    >
                        <Column field="Zaposleni" header={t("stampaDok.k_Zaposleni")} sortable style={{ minWidth: "180px" }} />
                        <Column field="JMBG" header={t("stampaDok.k_JMBG")} sortable style={{ minWidth: "140px" }} />
                        <Column field="TipDokumenta" header={t("stampaDok.k_Tip")} sortable style={{ minWidth: "250px" }} />
                        <Column field="redni_broj" header={t("stampaDok.k_RB")} sortable style={{ minWidth: "70px" }} />
                        <Column field="zavedeni_broj" header={t("stampaDok.k_ZavedeniBroj")} sortable style={{ minWidth: "120px" }} />
                        <Column field="DatumDokumenta" header={t("stampaDok.k_Datum")} sortable style={{ minWidth: "110px" }} />
                        <Column field="DatumPotpisa" header={t("stampaDok.k_DatumPotpisa")} sortable style={{ minWidth: "120px" }} />
                        <Column field="VaziOd" header={t("stampaDok.k_VaziOd")} style={{ minWidth: "110px" }} />
                        <Column field="VaziDo" header={t("stampaDok.k_VaziDo")} style={{ minWidth: "110px" }} />
                        <Column field="napomena" header={t("stampaDok.k_Napomena")} style={{ minWidth: "160px" }} />
                        <Column field="Zavedeno" header={t("stampaDok.k_Zavedeno")} sortable style={{ minWidth: "150px" }} />
                        <Column header={t("stampaDok.k_Dokument")} style={{ minWidth: "200px" }} body={kolonaFajlovi} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default UnosTab;
